import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { listenWebsocketEvents } from '@core/websocket';
import {
  indicateLoading,
  isArray,
  round,
  syncWithLocalStorage
} from '@core/helpers';

import {
  AVAILABLE_APPS_MAP,
  ApiResponse,
  AvailableApp,
  InventoryItem,
  SteamApp,
  WsInventoryItem
} from './items.entity';
import { InventoryService } from './inventory.service';

type InventoryApp = {
  count: number;
  app: AvailableApp;
};

@Injectable({
  providedIn: 'root'
})
export class InventoryState {
  private readonly inventoryService = inject(InventoryService);

  public readonly appId = signal<SteamApp|null>(null);
  public readonly inventory = signal<Record<number, InventoryItem>>({});
  public readonly ids = signal<number[]>([]);
  public readonly selectedIds = signal<number[]>([]);
  public readonly selectedInventoryAmountUsd = signal<number>(0);
  public readonly isDisabled = signal<boolean>(false);
  public readonly isHidden = signal<boolean>(false);
  public readonly isSelling = signal<boolean>(false);

  public readonly sortedInventory = computed(() => {
    const inventory = this.inventory();
    const appId = this.appId();
    const sortedInventory = Object.values(inventory).sort((a, b) => b.priceUsd - a.priceUsd);

    if(appId) {
      return sortedInventory.filter((invItem) => invItem.item.appId === appId);
    }

    return sortedInventory;
  });

  public readonly appsInInventory = computed(() => {
    const apps = Object.values(this._appsInInventory());

    if(apps.length > 1) {
      return apps;
    }

    return undefined;
  });

  private readonly _appsInInventory = computed(() => {
    const inventory = Object.values(this.inventory());
    const result: Record<number, InventoryApp> = {};

    inventory.forEach((invItem) => {
      const { appId } = invItem.item;

      if(!result[appId]) {
        const app = AVAILABLE_APPS_MAP[appId];

        result[appId] = { count: 0, app };
      }

      result[appId].count++;
    });

    return result;
  });

  public readonly selectedInvItems = computed(() => {
    const selectedIds = this.selectedIds();

    return this.sortedInventory().filter(invItem => selectedIds.includes(invItem.id));
  });

  public readonly isSelectedAll = computed(() => {
    const ids = this.ids();

    if(0 === ids.length) {
      return false;
    }

    return this.selectedIds().length === ids.length;
  });

  public readonly hasSelectedBonusItems = computed(() => {
    const selectedIds = this.selectedIds();

    return !!this.sortedInventory()
      .filter((invItem: InventoryItem) => selectedIds.includes(invItem.id))
      .find((invItem: InventoryItem) => invItem.isBonus);
  });

  private readonly wsEvents = listenWebsocketEvents({
    'inventory.new_items': (data) => this.onWsNewItems(data),
    'inventory.items_deleted': (data) => this.onWsItemsDeleted(data)
  });

  private readonly localStorageEffect = syncWithLocalStorage('inventory', {
    isHidden: this.isHidden,
    appId: this.appId
  });

  setAppId(appId: SteamApp|null): void {
    this.appId.set(appId);
  }

  loadInventory(): Observable<ApiResponse.InventoryResponse> {
    return this.inventoryService.getInventory()
      .pipe(
        tap((response) => {
          const inventory: Record<number, InventoryItem> = {};
          const ids: number[] = [];

          response.inventory.forEach((invItem: InventoryItem) => {
            invItem.priceUsd = Number(invItem.priceUsd);

            inventory[invItem.id] = invItem;
            ids.push(invItem.id);
          });

          this.inventory.set(inventory);
          this.ids.set(ids);

          this.resetAppIdIfNeeded();
        })
      );
  }

  buyItems(shopIds: number[], inventoryIds: number[]): Observable<void> {
    return this.inventoryService.buyItems(shopIds, inventoryIds)
      .pipe(
        tap(() => {
          this.selectedIds.set([]);
          this.selectedInventoryAmountUsd.set(0);
        })
      );
  }

  sellItem(invItemId: number): Observable<void> {
    return this.inventoryService.sellItem(invItemId)
      .pipe( indicateLoading(this.isSelling) );
  }

  sellMany(invItemIds: number[]): Observable<void> {
    return this.inventoryService.sellMany(invItemIds)
      .pipe( indicateLoading(this.isSelling) );
  }

  selectAllInventory(): void {
    const allIds = this.ids();

    if(0 === allIds.length || this.isDisabled()) {
      return;
    }

    const isSelectedAll = this.isSelectedAll();

    if(!isSelectedAll) {
      const selectedAmountUsd = this.sortedInventory()
        .reduce((prevValue, currentItem) => prevValue + currentItem.priceUsd, 0);

      this.selectedIds.set(allIds);
      this.selectedInventoryAmountUsd.set(round(selectedAmountUsd));
    } else {
      this.selectedIds.set([]);
      this.selectedInventoryAmountUsd.set(0);
    }
  }

  selectInventoryItem(invItem: InventoryItem): void {
    this.selectedIds.update((ids) => [...ids, invItem.id]);
    this.selectedInventoryAmountUsd.update((value) => round(value + invItem.priceUsd));
  }

  unselectInventoryItem(invItem: InventoryItem): void {
    this.selectedIds.update((ids) => ids.filter((id) => invItem.id !== id));
    this.selectedInventoryAmountUsd.update((value) => round(value - invItem.priceUsd));
  }

  resetSelectedItems(): void {
    this.selectedIds.set([]);
    this.selectedInventoryAmountUsd.set(0);
  }

  reset(): void {
    this.inventory.set({});
    this.ids.set([]);
    this.selectedIds.set([]);
    this.selectedInventoryAmountUsd.set(0);
    this.isDisabled.set(false);
  }

  private onWsNewItems(data: WsInventoryItem[]|WsInventoryItem): void {
    if(!isArray(data)) {
      data = [ data ];
    }

    data.forEach((invItem: InventoryItem) => {
      invItem = {
        ...invItem,
        priceUsd: round(invItem.priceUsd)
      };

      this.inventory.set({
        ...this.inventory(),
        [invItem.id]: invItem
      });

      this.ids.set([...this.ids(), invItem.id]);
    });
  }

  private onWsItemsDeleted(idsToDelete: number[]): void {
    let amountToSub = 0;

    const selectedItems = this.selectedInvItems();

    selectedItems.forEach((invItem: InventoryItem) => {
      if(idsToDelete.includes(invItem.id)) {
        amountToSub += invItem.priceUsd;
      }
    });

    this.selectedInventoryAmountUsd.update((invAmount) => invAmount - amountToSub);

    const inventory = { ...this.inventory() };

    idsToDelete.forEach((id) => {
      delete inventory[id];
    });

    this.inventory.set(inventory);
    this.ids.update((ids) => ids.filter((id) => !idsToDelete.includes(id)));
    this.selectedIds.update((ids) => ids.filter(id => !idsToDelete.includes(id)));

    this.resetAppIdIfNeeded();
  }

  private resetAppIdIfNeeded(): void {
    const appId = this.appId();
    const appsInInventory = this._appsInInventory();

    if(appId && !appsInInventory[appId]) {
      this.appId.set(null);
    }
  }
}
